import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import templatesService from "../../../../services/templates.service";
import {useEyeconxStore} from "../../../../store/eyeconxStore"
import { toast } from "react-toastify";

const CallTemplates = () => {
  const clinicId = useEyeconxStore((s) => s.user.clinicId);

  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editing, setEditing] = useState(false);

  const { register, handleSubmit, reset } = useForm();

  useEffect(() => {
    loadTemplates();
  }, [clinicId]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const res = await templatesService.getCallTemplates(clinicId);
      setTemplates(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load call templates.");
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateById = async (id) => {
    if (!id) return;
    setSelectedId(id);
    setEditing(false);

    try {
      setLoading(true);
      const res = await templatesService.getCallTemplateById(clinicId, id);
      const data = res.data;
      setSelectedTemplate(data);

      reset({
        content: data.content,
        xmlContent: data.xmlContent || "",
        status: data.status
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to load template.");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      await templatesService.updateCallTemplate(clinicId, selectedId, {
        content: data.content,
        xmlContent: data.xmlContent,
        status: data.status
      });

      toast.success("Call template updated.");
      setEditing(false);

      loadTemplates();
      loadTemplateById(selectedId);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update call template.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card border-0 shadow-sm my-4">
      <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <i className="bi bi-telephone-outbound me-2 text-primary"></i>
          Call Templates
        </h5>

        {selectedId && !editing && (
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => setEditing(true)}
          >
            Edit Template
          </button>
        )}

        {editing && (
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              setEditing(false);
              loadTemplateById(selectedId);
            }}
          >
            Cancel
          </button>
        )}
      </div>

      <div className="card-body">
        <div className="row">

          {/* LEFT SIDEBAR — LIST OF TEMPLATES */}
          <div className="col-md-4 border-end">
            <h6 className="text-muted mb-3">Template Types</h6>
            <div className="list-group">
              {templates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => loadTemplateById(t.id)}
                  className={`list-group-item list-group-item-action ${
                    selectedId === t.id ? "active" : ""
                  }`}
                >
                  <div className="fw-semibold">{t.reminderName}</div>
                  <small className="text-muted">
                    Updated: {t.modifiedAt}
                  </small>
                </button>
              ))}
            </div>
          </div>

          {/* RIGHT SIDE — EDITOR */}
          <div className="col-md-8">
            {!selectedTemplate ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-telephone-outbound display-4"></i>
                <p className="mt-3">Select a call template to view or edit.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Template Name */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Template Name
                  </label>
                  <p className="bg-light p-2 rounded">
                    {selectedTemplate.reminderName}
                  </p>
                </div>

                {/* CONTENT (Call Script Text) */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Call Script Content
                  </label>
                  <textarea
                    rows={8}
                    className="form-control"
                    disabled={!editing}
                    {...register("content")}
                  ></textarea>
                  <small className="text-muted">
                    Supports placeholders:  
                    <code>
                      {" {PATIENT_NAME} {DATE} {TIME} "}
                    </code>
                  </small>
                </div>

                {/* XML CONTENT (optional) */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    XML Content (Optional)
                  </label>
                  <textarea
                    rows={6}
                    className="form-control font-monospace"
                    disabled={!editing}
                    {...register("xmlContent")}
                  ></textarea>
                  <small className="text-muted">
                    Optional XML for IVR / Voice Provider (if applicable)
                  </small>
                </div>

                {/* Audio Preview */}
                {selectedTemplate.audioPath && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Audio Preview
                    </label>
                    <div className="bg-light p-3 rounded">
                      <audio controls src={selectedTemplate.audioPath} className="w-100"></audio>
                    </div>
                  </div>
                )}

                {/* Status Switch */}
                <div className="form-check form-switch mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    disabled={!editing}
                    {...register("status")}
                  />
                  <label className="form-check-label fw-semibold">
                    Template Active
                  </label>
                </div>

                {/* Save Button */}
                {editing && (
                  <button className="btn btn-primary" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      "Save Template"
                    )}
                  </button>
                )}
              </form>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default CallTemplates;
